import Constants from "../constants/Constants";

class typing {

    static EMPTY_STRING_REGEXP = /^\s*$/;

    // Checks if the value is a number. This function does not consider NaN a
    // number like many other `isNumber` functions do.
    static isNumber(value) {
        value = +value;
        return typeof value === 'number' && !isNaN(value);
    }

    // Returns false if the object is not a function
    static isFunction(value) {
        return typeof value === 'function';
    }

    // A simple check to verify that the value is an integer. Uses `isNumber`
    // and a simple modulo check.
    static isInteger(value) {
        return this.isNumber(value) && value % 1 === 0;
    }

    // Checks if the value is a boolean
    static isBoolean(value) {
        return typeof value === 'boolean';
    }

    // Uses the `Object` function to check if the given argument is an object.
    static isObject(obj) {
        return obj === Object(obj);
    }

    // Simply checks if the object is an instance of a date
    static isDate(obj) {
        return obj instanceof Date;
    }

    // Returns false if the object is `null` of `undefined`
    static isDefined(obj) {
        return obj !== null && obj !== undefined;
    }

    // Checks if the given argument is a promise. Anything with a `then`
    // function is considered a promise.
    static isPromise(p) {
        return !!p && this.isFunction(p.then);
    }

    static isJqueryElement(o) {
        return o && this.isString(o.jquery);
    }

    static isDomElement(o) {
        if (!o) {
            return false;
        }

        if (!o.querySelectorAll || !o.querySelector) {
            return false;
        }

        if (this.isObject(document) && o === document) {
            return true;
        }

        // http://stackoverflow.com/a/384380/699304
        /* istanbul ignore else */
        if (typeof HTMLElement === "object") {
            return o instanceof HTMLElement;
        } else {
            return o &&
                typeof o === "object" &&
                o !== null &&
                o.nodeType === 1 &&
                typeof o.nodeName === "string";
        }
    }

    static isEmpty(value) {
        let attr;

        // Null and undefined are empty
        if (!this.isDefined(value)) {
            return true;
        }

        // functions are non empty
        if (this.isFunction(value)) {
            return false;
        }

        // Whitespace only strings are empty
        if (this.isString(value)) {
            return this.EMPTY_STRING_REGEXP.test(value);
        }

        // For arrays we use the length property
        if (this.isArray(value)) {
            return value.length === 0;
        }

        // Dates have no attributes but aren't empty
        if (this.isDate(value)) {
            return false;
        }

        // If we find at least one property we consider it non empty
        if (this.isObject(value)) {
            for (attr in value) {
                return false;
            }
            return true;
        }

        return false;
    }

    static prettify(str) {
        if (this.isNumber(str)) {
            // If there are more than 2 decimals round it to two
            if ((str * 100) % 1 === 0) {
                return "" + str;
            } else {
                return parseFloat(Math.round(str * 100) / 100).toFixed(2);
            }
        }

        if (this.isArray(str)) {
            return str.map(function (s) { return this.prettify(s); }).join(", ");
        }

        if (this.isObject(str)) {
            return str.toString();
        }

        // Ensure the string is actually a string
        str = "" + str;

        return str
            // Splits keys separated by periods
            .replace(/([^\s])\.([^\s])/g, '$1 $2')
            // Removes backslashes
            .replace(/\\+/g, '')
            // Replaces - and - with space
            .replace(/[_-]/g, ' ')
            // Splits camel cased words
            .replace(/([a-z])([A-Z])/g, function (m0, m1, m2) {
                return "" + m1 + " " + m2.toLowerCase();
            })
            .toLowerCase();
    }

    static stringifyValue(value) {
        return this.prettify(value);
    }


    static isString(value) {
        return typeof value === 'string';
    }

    static isArray(value) {
        return {}.toString.call(value) === '[object Array]';
    }

    // Checks if the object is a hash, which is equivalent to an object that
    // is neither an array nor a function.
    static isHash(value) {
        return this.isObject(value) && !this.isArray(value) && !this.isFunction(value);
    }

    static contains(obj, value) {
        if (!this.isDefined(obj)) {
            return false;
        }
        if (this.isArray(obj)) {
            return obj.indexOf(value) !== -1;
        }
        return value in obj;
    }

    static unique(array) {
        if (!this.isArray(array)) {
            return array;
        }
        return array.filter(function (el, index, array) {
            return array.indexOf(el) === index;
        });
    }

    static isEmail(value) {
        if (!this.isDefined(value)) {
            return true;
        }
        if (!this.isString(value)) {
            return false;
        }
        const PATTERN = /^[a-z0-9\u007F-\uffff!#$%&'*+\\=?^_`{|}~-]+(?:\.[a-z0-9\u007F-\uffff!#$%&'*+\\=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,}$/i;
        if (!PATTERN.exec(value)) {
            return false;
        }

        return true;
    }

    static isUrl(value, options) {

        if (!this.isDefined(value)) {
            return true;
        }

        options = this.extend({}, this.options, options);

        //let message = options.message || this.message || "is not a valid url";
        let schemes = options.schemes || this.schemes || ['http', 'https'];
        let allowLocal = options.allowLocal || this.allowLocal || false;

        if (!this.isString(value)) {
            return false;
        }

        // https://gist.github.com/dperini/729294
        let regex =
            "^" +
            // protocol identifier
            "(?:(?:" + schemes.join("|") + ")://)" +
            // user:pass authentication
            "(?:\\S+(?::\\S*)?@)?" +
            "(?:";

        let tld = "(?:\\.(?:[a-z\\u00a1-\\uffff]{2,}))";

        if (allowLocal) {
            tld += "?";
        } else {
            regex +=
                // IP address exclusion
                // private & local networks
                "(?!(?:10|127)(?:\\.\\d{1,3}){3})" +
                "(?!(?:169\\.254|192\\.168)(?:\\.\\d{1,3}){2})" +
                "(?!172\\.(?:1[6-9]|2\\d|3[0-1])(?:\\.\\d{1,3}){2})";
        }

        regex +=
            // IP address dotted notation octets
            // excludes loopback network 0.0.0.0
            // excludes reserved space >= 224.0.0.0
            // excludes network & broacast addresses
            // (first & last IP address of each class)
            "(?:[1-9]\\d?|1\\d\\d|2[01]\\d|22[0-3])" +
            "(?:\\.(?:1?\\d{1,2}|2[0-4]\\d|25[0-5])){2}" +
            "(?:\\.(?:[1-9]\\d?|1\\d\\d|2[0-4]\\d|25[0-4]))" +
            "|" +
            // host name
            "(?:(?:[a-z\\u00a1-\\uffff0-9]-*)*[a-z\\u00a1-\\uffff0-9]+)" +
            // domain name
            "(?:\\.(?:[a-z\\u00a1-\\uffff0-9]-*)*[a-z\\u00a1-\\uffff0-9]+)*" +
            tld +
            ")" +
            // port number
            "(?::\\d{2,5})?" +
            // resource path
            "(?:[/?#]\\S*)?" +
            "$";

        const PATTERN = new RegExp(regex, 'i');
        if (!PATTERN.exec(value)) {
            return false;
        }

        return true;
    }

    static isUrlFriendly(value) {

        if (!this.isDefined(value)) {
            return true;
        }

        if (!this.isString(value)) {
            return false;
        }

        let regex = "^[a-zA-Z0-9]([a-zA-Z0-9][a-zA-Z0-9]*\\.)*[a-zA-Z0-9]*[a-zA-Z0-9-]*[[a-zA-Z0-9]+$";
        const PATTERN = new RegExp(regex, 'i');
        if (!PATTERN.exec(value)) {
            return false;
        }

        return true;

    }

    static isAlphaNumeric(value) {

        if (!this.isDefined(value)) {
            return true;
        }

        if (!this.isString(value)) {
            return false;
        }

        let regex = "^[a-zA-Z0-9 ]*$";
        const PATTERN = new RegExp(regex, 'i');
        if (!PATTERN.exec(value)) {
            return false;
        }

        return true;

    }

    static extend(obj) {
        [].slice.call(arguments, 1).forEach(function (source) {
            for (var attr in source) {
                obj[attr] = source[attr];
            }
        });
        return obj;
    }

    static clone(obj) {
        if (null == obj || "object" != typeof obj) return obj;
        var copy = obj.constructor();
        for (var attr in obj) {
            if (obj.hasOwnProperty(attr)) copy[attr] = obj[attr];
        }
        return copy;
    }

    static forEachKeyInKeypath(object, keypath, callback) {
        if (!this.isString(keypath)) {
            return undefined;
        }

        let key = ""
            , i
            , escape = false;

        for (i = 0; i < keypath.length; ++i) {
            switch (keypath[i]) {
                case '.':
                    if (escape) {
                        escape = false;
                        key += '.';
                    } else {
                        object = callback(object, key, false);
                        key = "";
                    }
                    break;

                case '\\':
                    if (escape) {
                        escape = false;
                        key += '\\';
                    } else {
                        escape = true;
                    }
                    break;

                default:
                    escape = false;
                    key += keypath[i];
                    break;
            }
        }

        return callback(object, key, true);
    }

    static convertToDateToMMDDDYYYY(input) {
        function pad(s) { return (s < 10) ? '0' + s : s; }
        const d = new Date(input);
        return [pad(d.getMonth() + 1), pad(d.getDate()), d.getFullYear()].join('/');
    }


    static convertToDateToYYYY_MM_DD(input) {
        function pad(s) { return (s < 10) ? '0' + s : s; }
        const d = new Date(input);
        return [d.getFullYear(), pad(d.getMonth() + 1), pad(d.getDate())].join('-');
    }

    static convertDateToBBDDString(input) {
        if (input) {
            return input.getDate() + '/' + (input.getMonth() + 1) + '/' + input.getFullYear()
        }
        else {
            return undefined;
        }
    }

    static convertToTimeAMPM(input) {
        return new Date(input).toLocaleString('en-US',
            {
                hour: 'numeric',
                minute: 'numeric',
                second: 'numeric',
                hour12: true
            });
    }

    static convertToDateShortMonth(input) {
        let d;
        if (this.isDate(input)) {
            d = input;
        }
        else if (input.startsWith('\\Date')) {
            d = new Date(input.match(/\d+/)[0] * 1);
        }
        else {
            d = new Date(input || null);
        }
        return Constants.monthNames[d.getMonth()].substring(0, 3) + ' ' + d.getDate() + ', ' + d.getFullYear();
    }

    static convertToDateShortMonthWithTime(input) {
        let d;
        if (input.startsWith('\\Date')) {
            d = new Date(input.match(/\d+/)[0] * 1);
        }
        else {
            d = new Date(input || null);
        }

        let h = typing.addZero(d.getHours());
        let m = typing.addZero(d.getMinutes());
        let s = typing.addZero(d.getSeconds());
               
        return Constants.monthNames[d.getMonth()].substring(0, 3) + ' ' + d.getDate() + ', ' + d.getFullYear() + ' ' + h + ':' + m + ':'+s;

    }

    static addZero(number) {
        if (number < 10) {
            number = "0" + number;
        }
        return number;
    }

    //static convertToDateLongMonth(input) {
    //    const d = new Date(input || null);
    //    return CMS.CommonEntities.monthNames[d.getMonth()] + ' ' + d.getDate() + ', ' + d.getFullYear();
    //}

    static numberWithCommas(number) {
        return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    }

    static stripTags(string, array) {
        return array ? string.split("<").filter(function (val) { return f(array, val); }).map(function (val) { return f(array, val); }).join("") : string.split("<").map(function (d) { return d.split(">").pop(); }).join("");
        function f(array, value) {
            return array.map(function (d) { return value.includes(d + ">"); }).indexOf(true) !== -1 ? "<" + value : value.split(">")[1];
        }
    }

    static areEqual(a, b) {
        return JSON.stringify(a) === JSON.stringify(b);
    }

    static convertToSlug(text) {
        if (text) {
            return text
                .toLowerCase()
                .replace(/[^\w ]+/g, '')
                .replace(/ +/g, '-');
        }
        else {
            return '';
        }
    }

    static capitalizeFirstLetter(input) {
        return input.charAt(0).toUpperCase() + input.slice(1);
    }

    static toTitleCase(input) {
        return input.replace(/\w\S*/g, function (txt) { return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase(); });
    }

    static getBase64ForAFile(file) {
        return new Promise(function (resolve, reject) {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = function () {
                resolve({
                    name: file.name,
                    value: reader.result.split("base64,")[1]
                });
            };
        });
    }

    static getBase64(files, callback) {

        const filesJson = [];
        for (let i = 0; i < files.length; i++) {
            filesJson.push(typing.getBase64ForAFile(files[i]));
        }

        Promise.all(filesJson).then(
            result => {
                if (callback) {
                    callback(result);
                }
            }
        );
    }

    static spread() {
        let result = [];
        for (let i = 0; i < arguments.length; i++) {
            if (typing.isArray(arguments[i])) {
                result = result.concat(arguments[i]);
            }
            else {
                result.push(arguments[i])
            }
        }
        return result;
    }

    static shorten(originalString, maxLength) {
        if (originalString && originalString.length <= maxLength) {
            return originalString;
        }
        else if (originalString) {
            return originalString.substring(0, maxLength - 4) + '...';
        }
        return undefined;
    }

    static formatAmount(amount, isDollar) {
        if (isDollar) {
            return '$' + amount;
        }
        else {
            return amount + '€';
        }
    }

    static formatPorcent(number) {
     return `${(number * 100).toFixed(2)}%`;
    }

    static getIndex(array, predicate) {
        for (let i = 0; i < array.length;) {
            if (predicate(array[i])) {
                return i;
            }
            i++;
        }
        return -1;
    }

    static convertStringToListInt(string) {
        const result = [];
        const resultSplit = string.split(',');
        for (var i = 0; i < resultSplit.length; i++) {
            let number = resultSplit[i] * 1;
            if (number > 0) {
                result.push(number);
            }
        }
        return result;
    }

    static dateDiffInDays (a, b) {
        var _MS_PER_DAY = 1000 * 60 * 60 * 24;

        // Discard the time and time-zone information.
        var utc1 = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate());
        var utc2 = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate());

        return Math.abs(Math.floor((utc2 - utc1) / _MS_PER_DAY));
    }
}

export default typing;